import { PlusIcon, ZapIcon } from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

import { resolveQuickReplyText } from '@/features/form-responses/utils/quick-replies-util';

import SimpleDropdown, { DropdownOption } from '@/shared/components/SimpleDropdown';
import { COPY } from '@/shared/constants/copies';
import { ASSIGNMENT_LINK_VARIABLE } from '@/shared/constants/message-constants';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { isNonDeletableQuickReply } from '@/shared/constants/quick-replies';
import { updateQuickReplies } from '@/shared/services/forms';
import { IQuickReply } from '@/shared/services/forms/forms.types';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { parseHtmlToPlainText } from '@/shared/utils/html-utils';

import { cn } from '@/utils/styles';

import { IDropdownViewProps } from './dropdown-view.types';
import QuickRepliesModal from './quick-replies-modal';

function DropdownView({
	variableValues,
	onSelect,
	formId,
	customClassName,
	overrideCmdEnter,
	quickReplies: initialQuickReplies = [],
	onUpdateQuickReplies,
	onOpenChange,
	isBulkAction = false,
	supportsAssignmentLink = false,
}: IDropdownViewProps) {
	const [open, setOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleSaveReplies = useCallback(
		async (replies: { text: string; name: string; priority: number }[]) => {
			return await updateQuickReplies({
				form_id: formId!,
				quick_replies: replies,
			});
		},
		[formId]
	);

	const quickReplies: (IQuickReply & { parsedText: string })[] = useMemo(
		() =>
			initialQuickReplies.map((reply) => ({
				...reply,
				parsedText: parseHtmlToPlainText(reply.text),
				text: reply.text,
				isDefault: isNonDeletableQuickReply(reply.name),
			})),
		[initialQuickReplies]
	);

	const handleManageClick = useCallback(() => {
		const openedVia = isBulkAction
			? 'bulk_reply_nudge'
			: overrideCmdEnter
				? 'messages_page'
				: 'focus_mode';
		const feature =
			openedVia === 'messages_page' ? MIXPANEL_APP_FEATURES.MESSAGES : MIXPANEL_APP_FEATURES.FORM;
		emitMixpanelEvent(mixpanelEvents.VIEWED_REPLY_TEMPLATES_MODAL, {
			feature,
			opened_via: openedVia,
		});
		setIsModalOpen(true);
		setOpen(false);
		onOpenChange?.(true);
	}, [isBulkAction, overrideCmdEnter, onOpenChange]);

	const handleQuickRepliesModalClose = useCallback(() => {
		setIsModalOpen(false);
		onOpenChange?.(false);
	}, [onOpenChange]);

	const handleTriggerKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && overrideCmdEnter) {
				setOpen(false);
				e.preventDefault();
			}
		},
		[overrideCmdEnter]
	);

	const handleOpenChange = useCallback(
		(isOpen: boolean) => {
			setOpen(isOpen);
			onOpenChange?.(isOpen);
		},
		[onOpenChange]
	);

	const dropdownOptions: DropdownOption[] = useMemo(() => {
		const options: DropdownOption[] = [];

		if (formId) {
			options.push({
				label: 'Add or edit reply shortcuts',
				value: 'manage',
				icon: <PlusIcon className="size-4" />,
				onClick: handleManageClick,
				showSeparator: quickReplies.length > 0,
			});
		}

		quickReplies.forEach((quickReply) => {
			const parsed = resolveQuickReplyText(quickReply.parsedText, variableValues);

			const usesAssignmentLink = quickReply.text.includes(ASSIGNMENT_LINK_VARIABLE.token);

			options.push({
				label: quickReply.name,
				sublabel: isBulkAction ? quickReply.parsedText : parsed,
				value: quickReply.name,
				tooltip:
					!supportsAssignmentLink && usesAssignmentLink
						? COPY.messaging.quickReplyNeedsAssignment
						: undefined,
				onClick: () => {
					onSelect(isBulkAction ? quickReply.text : parsed);
					setOpen(false);
				},
			});
		});

		return options;
	}, [
		formId,
		quickReplies,
		isBulkAction,
		variableValues,
		handleManageClick,
		onSelect,
		supportsAssignmentLink,
	]);

	useEffect(() => {
		const keydownHandler = (e: KeyboardEvent) => {
			if (!overrideCmdEnter || !open) return;

			if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		document.addEventListener('keydown', keydownHandler);

		return () => document.removeEventListener('keydown', keydownHandler);
	}, [overrideCmdEnter, open]);

	return (
		<>
			<SimpleDropdown
				open={open}
				onOpenChange={handleOpenChange}
				options={dropdownOptions}
				modal={true}
				dropdownAlign="start"
				className="mb-1 w-80"
				maxHeight={300}
				truncateLongOptions
			>
				<Button
					size="icon"
					variant="ghost"
					className={cn('mr-3 size-4 p-0 text-secondary hover:bg-inherit', customClassName)}
					onKeyDown={handleTriggerKeyDown}
				>
					<ZapIcon className="size-4" />
				</Button>
			</SimpleDropdown>

			{formId && isModalOpen && (
				<QuickRepliesModal
					isOpen={isModalOpen}
					onClose={handleQuickRepliesModalClose}
					quickReplies={quickReplies}
					onSaveReplies={handleSaveReplies}
					onUpdateQuickReplies={onUpdateQuickReplies}
				/>
			)}
		</>
	);
}

export default DropdownView;
